/*
Copyright (c) 2025 Mathilde Meyer, Naziha Beghdadi
Licensed under the MIT License (see LICENSE.txt file for details)
*/

import JSZip from "jszip";

import React, { useEffect, useRef, useImperativeHandle } from "react";
import { DataSet, Timeline as VisTimeline } from "vis-timeline/standalone";
import "vis-timeline/styles/vis-timeline-graph2d.min.css";
import titles from "./titles";
import html2canvas from "html2canvas";
import DownloadIcon from "@mui/icons-material/Download";
import { Box, Button } from "@mui/material";
import {
  buildExperimentTimelineOverview,
  getInterventionOccurrences,
  getInterventionTypeLabel,
  getTimelineLegendEntries,
} from "../utils/timelineOverview";

const BASE_DATE = new Date("2025-04-01");

function getDateFromDay(dayString) {
  const day = parseInt(dayString, 10);
  if (Number.isNaN(day)) return null;
  const date = new Date(BASE_DATE);
  date.setDate(BASE_DATE.getDate() + day - 1);
  return date.toISOString().split("T")[0];
}

const buildIconBubble = (icon, size, alt) => {
  if (typeof icon === "string" && icon.endsWith(".svg")) {
    return `
      <div style="width:30px; height:30px; border-radius:50%; border:1px solid #d7d7d7; background:#ffffff; display:flex; align-items:center; justify-content:center; box-shadow:0 1px 2px rgba(0,0,0,0.12);">
        <img src="${icon}" width="${size}" height="${size}" alt="${alt}" />
      </div>
    `;
  }

  return `<span style="font-size: 0.9rem; font-style: italic;">${icon || "?"}</span>`;
};

const TimelineComponent = React.forwardRef(({ data, index, projectName, duration, title }, ref) => {
  const containerRef = useRef(null);
  const timelineRef = useRef(null);
  const captureRef = useRef(null);
  const pxPerDayRef = useRef(32);

  useImperativeHandle(ref, () => ({
    capture: async () => {
      if (!captureRef.current) return null;
      const captureEl = captureRef.current;
      const originalPadding = captureEl.style.paddingBottom;
      captureEl.style.paddingBottom = "20px";
      await new Promise((resolve) => setTimeout(resolve, 300));
      const canvas = await html2canvas(captureEl, { scale: 3, useCORS: true });
      captureEl.style.paddingBottom = originalPadding;
      const blob = await new Promise((resolve) => canvas.toBlob(resolve, "image/png"));
      const arrayBuffer = await blob.arrayBuffer();

      return {
        data: new Uint8Array(arrayBuffer),
        width: canvas.width,
        height: canvas.height,
      };
    },
  }));

  const experiment = data?.[0] || {};
  const overview = buildExperimentTimelineOverview(experiment, index);
  const usedIcons = getTimelineLegendEntries(experiment);

  useEffect(() => {
    if (!containerRef.current || !duration || Number.isNaN(duration)) return;

    const items = [];

    data.forEach((entry, entryIndex) => {
      const prepType = entry.preparation;
      const prepDays = parseInt(entry.preparationDays || "0", 10);
      const acclimatation = entry.acclimatation;
      const acclimatationDays = parseInt(entry.acclimatationDays || "0", 10);

      if (acclimatation && acclimatationDays > 0) {
        const acclimStartDate = getDateFromDay(1);
        const acclimEndDate = getDateFromDay(acclimatationDays + 1);

        if (acclimStartDate && acclimEndDate) {
          items.push({
            id: `${entryIndex}-acclim`,
            content: `
              <div style="display:flex; justify-content: center; align-items: center; height: 100%;">
                <span style="font-size: 0.72rem; font-weight: 700; color: #24404f; background-color: #dff1fb; border: 1px solid #9ec9dd; padding: 5px 10px; border-radius: 999px;">
                  Acclimatation
                </span>
              </div>
            `,
            start: acclimStartDate,
            end: acclimEndDate,
            type: "range",
            group: "preparation",
          });
        }
      }

      if (prepType && prepType !== "no_preparation" && prepDays > 0) {
        let prepStartDay = 1;
        if (acclimatation === "yes" && acclimatationDays > 0) {
          prepStartDay = acclimatationDays + 1;
        }

        const prepStartDate = getDateFromDay(prepStartDay);
        const prepEndDate = getDateFromDay(prepStartDay + prepDays);

        if (prepStartDate && prepEndDate) {
          items.push({
            id: `${entryIndex}-prep`,
            content: `
              <div style="display:flex; justify-content: center; align-items: center; height: 100%;">
                <div style="display:flex; align-items:center; gap:8px; padding:4px 10px; border:1px solid #e3b96f; background:#fff4da; border-radius:999px;">
                  <img src="/icons/SpecialPreparation.svg" width="18" height="18" alt="Special preparation" />
                  <span style="font-size:0.72rem; font-weight:700; color:#6b4a00;">Preparation</span>
                </div>
              </div>
            `,
            start: prepStartDate,
            end: prepEndDate,
            type: "range",
            group: "preparation",
          });
        }
      }

      entry.interventions?.forEach((intervention, interventionIndex) => {
        const typeLabel = getInterventionTypeLabel(intervention);
        const titleMatch = titles.find((item) => item.label === intervention.interventionType);
        const icon = titleMatch?.icon || "/icons/PointInterrogation.svg";
        const imgSize = intervention.interventionType === "Other" ? 15 : 24;

        getInterventionOccurrences(intervention).forEach((day, occurrenceIndex) => {
          const date = getDateFromDay(day);
          if (!date) return;

          const occurrenceSuffix =
            getInterventionOccurrences(intervention).length > 1 ? ` (occurrence ${occurrenceIndex + 1})` : "";

          items.push({
            id: `${entryIndex}-${interventionIndex}-${occurrenceIndex}`,
            content: `
              <div style="display:flex; align-items: center; justify-content:center; width:100%; height:100%;">
                ${buildIconBubble(icon, imgSize, typeLabel)}
              </div>
            `,
            start: date,
            group: "interventions",
            title: `${typeLabel}${occurrenceSuffix}`,
          });
        });
      });
    });

    const startDate = new Date(BASE_DATE);
    startDate.setDate(startDate.getDate() - 1);
    const endDate = new Date(BASE_DATE);
    endDate.setDate(endDate.getDate() + duration);

    if (containerRef.current) {
      containerRef.current.style.width = `${(duration + 1) * pxPerDayRef.current}px`;
    }

    const groups = new DataSet([
      { id: "preparation", content: '<span style="font-weight:700; color:#24404f;">Preparation</span>' },
      { id: "interventions", content: '<span style="font-weight:700; color:#5d2e00;">Interventions</span>' },
    ]);

    const options = {
      start: startDate,
      end: endDate,
      showCurrentTime: false,
      zoomable: false,
      moveable: false,
      selectable: false,
      stack: true,
      showMajorLabels: false,
      showMinorLabels: false,
      orientation: "top",
      verticalScroll: false,
      margin: {
        item: { horizontal: 12, vertical: 14 },
        axis: 12,
      },
      timeAxis: {
        scale: "day",
        step: 1,
      },
    };

    const timeline = new VisTimeline(containerRef.current, new DataSet(items), groups, options);
    timelineRef.current = timeline;

    return () => {
      timeline.destroy();
    };
  }, [data, duration]);

  const handleDownloadZip = async () => {
    if (!captureRef.current) return;

    const captureEl = captureRef.current;
    const originalPadding = captureEl.style.paddingBottom;
    captureEl.style.paddingBottom = "20px";
    await new Promise((resolve) => setTimeout(resolve, 300));
    const canvas = await html2canvas(captureEl, { scale: 3, useCORS: true });
    captureEl.style.paddingBottom = originalPadding;

    const blob = await new Promise((resolve) => canvas.toBlob(resolve, "image/png"));
    const zip = new JSZip();
    zip.file("timeline.png", blob);
    zip.file("formulaire.json", JSON.stringify(data[0], null, 2));

    const zipBlob = await zip.generateAsync({ type: "blob" });
    const url = URL.createObjectURL(zipBlob);
    const a = document.createElement("a");
    a.href = url;

    const safeName = projectName?.trim().replace(/\s+/g, "-").toLowerCase() || "projet";
    a.download = `${safeName}-timeline-${index + 1}.zip`;
    a.click();
  };

  const pxPerDay = pxPerDayRef.current;
  const timelineWidth = `${(duration + 1) * pxPerDay}px`;

  return (
    <>
      <div style={{ overflowX: "auto", width: "100%" }}>
        <div
          ref={captureRef}
          style={{
            background: "#fff",
            border: "1px solid #e3e3e3",
            borderRadius: "16px",
            padding: "20px 18px 18px",
            boxShadow: "0 6px 18px rgba(0,0,0,0.06)",
          }}
        >
          {title && (
            <div style={{ textAlign: "center", fontWeight: "bold", fontSize: "1.2rem", marginBottom: "12px", color: "#1f1f1f" }}>
              {title}
            </div>
          )}

          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))",
              gap: "10px",
              width: timelineWidth,
              margin: "0 auto 16px",
            }}
          >
            <div style={{ border: "1px solid #d7e6ef", background: "#f4fbff", borderRadius: "12px", padding: "10px 12px" }}>
              <div style={{ fontSize: "0.72rem", textTransform: "uppercase", color: "#466171", letterSpacing: "0.04em" }}>Duration</div>
              <div style={{ fontSize: "1rem", fontWeight: 700 }}>{overview.durationDays || duration || 0} days</div>
            </div>
            <div style={{ border: "1px solid #d7e6ef", background: "#f8fbfc", borderRadius: "12px", padding: "10px 12px" }}>
              <div style={{ fontSize: "0.72rem", textTransform: "uppercase", color: "#466171", letterSpacing: "0.04em" }}>Cohorts</div>
              <div style={{ fontSize: "1rem", fontWeight: 700 }}>{overview.cohorts || "Not specified"}</div>
            </div>
            <div style={{ border: "1px solid #d7e6ef", background: "#fffaf2", borderRadius: "12px", padding: "10px 12px" }}>
              <div style={{ fontSize: "0.72rem", textTransform: "uppercase", color: "#705f3a", letterSpacing: "0.04em" }}>Repetitions</div>
              <div style={{ fontSize: "1rem", fontWeight: 700 }}>{overview.repetitions || "Not specified"}</div>
            </div>
          </div>

          <div
            style={{
              display: "flex",
              width: timelineWidth,
              margin: "0 auto",
              fontSize: "0.78rem",
              color: "#5c6b75",
              userSelect: "none",
              overflow: "hidden",
              borderBottom: "1px solid #e4edf1",
              paddingBottom: "6px",
            }}
          >
            {Array.from({ length: duration + 1 }, (_, dayIndex) => (
              <div
                key={dayIndex}
                style={{
                  width: `${pxPerDay}px`,
                  textAlign: "center",
                  padding: "4px 0",
                  boxSizing: "border-box",
                }}
              >
                {dayIndex === 0 ? "Day" : dayIndex}
              </div>
            ))}
          </div>

          <div
            ref={timelineRef}
            style={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              margin: "12px auto 0",
            }}
          >
            <div
              ref={containerRef}
              style={{
                height: "200px",
                border: "1px solid #e4edf1",
                borderRadius: "12px",
                background: "#fcfefe",
              }}
            />
          </div>

          {usedIcons.length > 0 && (
            <div style={{ width: timelineWidth, margin: "16px auto 0" }}>
              <div style={{ fontWeight: 700, marginBottom: "8px", color: "#253640" }}>Legend</div>
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
                  gap: "10px",
                }}
              >
                {usedIcons.map((entry) => (
                  <div
                    key={entry.label}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "8px",
                      lineHeight: "1.2",
                      border: "1px solid #e4edf1",
                      borderRadius: "10px",
                      padding: "8px 10px",
                      background: "#fff",
                    }}
                  >
                    {entry.icon ? (
                      <img src={entry.icon} alt={entry.label} width={20} height={20} style={{ display: "inline-block", verticalAlign: "middle" }} />
                    ) : (
                      <span style={{ fontSize: "0.85rem", fontWeight: 700, color: "#24404f", background: "#dff1fb", padding: "4px 8px", borderRadius: "999px" }}>
                        A
                      </span>
                    )}
                    <span style={{ fontSize: "0.84rem" }}>{entry.label}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {overview.keySteps.length > 0 && (
            <div style={{ width: timelineWidth, margin: "16px auto 0" }}>
              <div style={{ fontWeight: 700, marginBottom: "8px", color: "#253640" }}>Key timeline checkpoints</div>
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
                  gap: "8px",
                }}
              >
                {overview.keySteps.slice(0, 8).map((step, stepIndex) => (
                  <div
                    key={`${step.day}-${step.label}-${stepIndex}`}
                    style={{
                      border: "1px solid #e4edf1",
                      borderRadius: "10px",
                      padding: "8px 10px",
                      background: "#fbfcfd",
                    }}
                  >
                    <div style={{ fontSize: "0.74rem", textTransform: "uppercase", color: "#587081", letterSpacing: "0.04em" }}>
                      Day {step.day}
                    </div>
                    <div style={{ fontSize: "0.84rem", fontWeight: 600, marginTop: "3px", color: "#2f2f2f" }}>
                      {step.label}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>

      <Box display="flex" justifyContent="center" mt={3}>
        <Button
          variant="outlined"
          onClick={handleDownloadZip}
          startIcon={<DownloadIcon />}
          sx={{
            backgroundColor: "primary.dark",
            color: "#fff",
            "&:hover": {
              backgroundColor: "#fff",
              color: "primary.dark",
            },
          }}
        >
          {`Download ZIP for Experiment ${index + 1}`}
        </Button>
      </Box>
    </>
  );
});

export default TimelineComponent;
